/**
 * Gift Catalog Configuration for Poker Reborn
 *
 * Defines all available gifts, organized by tier and category.
 * Must stay in sync with server catalog:
 * poker_platform/services/poker_services/apps/acc/lib/acc/gift_catalog.ex
 *
 * Emoji characters are used as placeholder sprites until real art assets
 * are created via the studio/gifts/ pipeline.
 *
 * @see src/types/gifts.ts - Type definitions
 * @see tasks/done/T42_TABLE_GIFTS_PLAN.md - Feature plan
 */

import type { GiftDefinition, GiftCategory, GiftTier } from '@/types/gifts';

// =============================================================================
// GIFT CATALOG
// =============================================================================

/**
 * Complete gift catalog — synced with server's Acc.GiftCatalog.
 *
 * TIER 1 — FLASH (free): Quick icon pulse at receiver
 * TIER 2 — FLYER (chips, 3 free/day): Bezier arc flight + sparkle
 * TIER 3 — IMPACT (chips): Flight + localized effect on receiver avatar
 * TIER 4 — SPECTACLE (gold): Partial/full screen takeover
 */
export const GIFT_CATALOG: readonly GiftDefinition[] = [
  // ── Tier 1: Flash (Free, unlimited) ──────────────────────────────────────
  {
    id: 'thumbsup',
    name: 'Thumbs Up',
    emoji: '👍',
    tier: 1,
    category: 'emoji',
    price: 0,
    currency: 'chips',
  },
  {
    id: 'heart',
    name: 'Heart',
    emoji: '❤️',
    tier: 1,
    category: 'emoji',
    price: 0,
    currency: 'chips',
  },
  {
    id: 'laugh',
    name: 'Laugh',
    emoji: '😂',
    tier: 1,
    category: 'emoji',
    price: 0,
    currency: 'chips',
  },
  {
    id: 'angry',
    name: 'Angry',
    emoji: '😡',
    tier: 1,
    category: 'emoji',
    price: 0,
    currency: 'chips',
  },

  // ── Tier 2: Flyer (Chips, 3 free per day) ────────────────────────────────
  {
    id: 'egg',
    name: 'Egg',
    emoji: '🥚',
    tier: 2,
    category: 'food',
    price: 500,
    currency: 'chips',
    soundFly: 'whoosh',
    soundImpact: 'splat',
  },
  {
    id: 'beer',
    name: 'Beer',
    emoji: '🍺',
    tier: 2,
    category: 'food',
    price: 500,
    currency: 'chips',
    soundFly: 'whoosh',
    soundImpact: 'clink',
  },
  {
    id: 'pizza',
    name: 'Pizza',
    emoji: '🍕',
    tier: 2,
    category: 'food',
    price: 1000,
    currency: 'chips',
    soundFly: 'whoosh',
    soundImpact: 'splat',
  },
  {
    id: 'chicken',
    name: 'Chicken',
    emoji: '🐔',
    tier: 2,
    category: 'food',
    price: 1000,
    currency: 'chips',
    soundFly: 'whoosh',
    soundImpact: 'splat',
  },
  {
    id: 'tissues',
    name: 'Tissues',
    emoji: '🧻',
    tier: 2,
    category: 'taunt',
    price: 1000,
    currency: 'chips',
    soundFly: 'whoosh',
    soundImpact: 'splat',
  },

  // ── Tier 3: Impact (Chips, no freebies) ──────────────────────────────────
  {
    id: 'boxing_glove',
    name: 'Boxing Glove',
    emoji: '🥊',
    tier: 3,
    category: 'taunt',
    price: 5000,
    currency: 'chips',
    soundFly: 'whoosh',
    soundImpact: 'pow',
  },
  {
    id: 'water_bucket',
    name: 'Water Bucket',
    emoji: '🪣',
    tier: 3,
    category: 'taunt',
    price: 5000,
    currency: 'chips',
    soundFly: 'whoosh',
    soundImpact: 'splash',
  },
  {
    id: 'bomb',
    name: 'Bomb',
    emoji: '💣',
    tier: 3,
    category: 'taunt',
    price: 5000,
    currency: 'chips',
    soundFly: 'whoosh',
    soundImpact: 'boom',
  },
  {
    id: 'bullseye',
    name: 'Bullseye',
    emoji: '🎯',
    tier: 3,
    category: 'taunt',
    price: 3000,
    currency: 'chips',
    soundFly: 'whoosh',
    soundImpact: 'thwack',
  },

  // ── Tier 4: Spectacle (Gold currency) ────────────────────────────────────
  {
    id: 'fireworks',
    name: 'Fireworks',
    emoji: '🎆',
    tier: 4,
    category: 'celebration',
    price: 50,
    currency: 'gold',
    soundFly: 'whoosh',
    soundImpact: 'fanfare',
  },
  {
    id: 'make_it_rain',
    name: 'Make It Rain',
    emoji: '💸',
    tier: 4,
    category: 'celebration',
    price: 100,
    currency: 'gold',
    soundFly: 'whoosh',
    soundImpact: 'cha_ching',
  },
  {
    id: 'trophy',
    name: 'Trophy',
    emoji: '🏆',
    tier: 4,
    category: 'celebration',
    price: 75,
    currency: 'gold',
    soundFly: 'whoosh',
    soundImpact: 'fanfare',
  },
  {
    id: 'guitar',
    name: 'AC/DC Guitar',
    emoji: '🎸',
    tier: 4,
    category: 'premium',
    price: 150,
    currency: 'gold',
    soundFly: 'whoosh',
    soundImpact: 'guitar_riff',
  },
] as const;

// =============================================================================
// CATALOG HELPERS
// =============================================================================

/** Lookup a gift by ID. Returns undefined if not found. */
export function getGiftById(giftId: string): GiftDefinition | undefined {
  return GIFT_CATALOG.find((gift) => gift.id === giftId);
}

/** Get all gifts in a specific category. */
export function getGiftsByCategory(category: GiftCategory): readonly GiftDefinition[] {
  return GIFT_CATALOG.filter((gift) => gift.category === category);
}

/** Get all gifts of a specific tier. */
export function getGiftsByTier(tier: GiftTier): readonly GiftDefinition[] {
  return GIFT_CATALOG.filter((gift) => gift.tier === tier);
}

/** Get all gift categories that have at least one gift. */
export function getAvailableCategories(): GiftCategory[] {
  const categories = new Set<GiftCategory>();
  for (const gift of GIFT_CATALOG) {
    categories.add(gift.category);
  }
  return [...categories];
}

// =============================================================================
// TIER ANIMATION DURATIONS
// =============================================================================

/** Flight duration in ms per tier. Includes arc + hold at destination. */
export const GIFT_FLY_DURATION: Record<GiftTier, number> = {
  1: 0,      // Tier 1 has no flight — instant flash
  2: 1500,   // Standard arc
  3: 1000,   // Faster = punchier
  4: 1200,   // Moderate — spectacle happens after landing
};

/** Impact effect duration in ms per tier. */
export const GIFT_IMPACT_DURATION: Record<GiftTier, number> = {
  1: 400,   // Quick pulse
  2: 600,   // Sparkle burst
  3: 800,   // Localized effect
  4: 1200,  // Extended spectacle
};

/** Toast display duration in ms. */
export const GIFT_TOAST_DURATION = 3000;
